import {Reducer, UnknownAction} from 'redux';

import {REHYDRATE_RTK_QUERY} from '../constants/REHYDRATE_RTK_QUERY';
import {saveToIDB} from '../storage';
import {GenericRTKQState} from '../types/GenericRTKQState';
import {PersistEnhancerConfig} from '../types/PersistEnhancerConfig';
import {sanitizeRtkQueryState} from '../utils/sanitizeRtkQueryState';

/**
 * Creates a reducer enhancer that persists the reducer's state to IndexedDB
 */
export const createPersistReducerEnhancer = <
  S extends GenericRTKQState,
  A extends UnknownAction & {payload?: {reducerPath?: string}},
>(
  config: PersistEnhancerConfig<S>
) => {
  const {key, transforms = [], shouldPersist = () => true, debounceTime = 1000} = config;

  let lastState: S | undefined = undefined;
  let persistTimeout: NodeJS.Timeout | null = null;
  let activeSavePromise: Promise<void> | null = null; // Track active save to prevent races

  return (reducer: Reducer<S, A>): Reducer<S, A> =>
    (state, action) => {
      // Apply the reducer to get the next state
      const nextState = reducer(state, action);

      if (action.type === REHYDRATE_RTK_QUERY && action.payload?.reducerPath === key) {
        return nextState;
      }

      // Only persist if the state has changed and passes the shouldPersist check
      if (nextState !== lastState && shouldPersist(nextState)) {
        lastState = nextState;

        if (persistTimeout) {
          clearTimeout(persistTimeout);
        }

        persistTimeout = setTimeout(async () => {
          // Wait for any active save to complete first (prevents race condition)
          if (activeSavePromise) {
            await activeSavePromise.catch(() => {}); // Ignore errors from previous save
          }

          let stateToSave = sanitizeRtkQueryState(nextState);
          transforms.forEach((transform) => {
            stateToSave = transform.serialize(stateToSave);
          });

          // Track this save operation
          activeSavePromise = saveToIDB(key, stateToSave);
          await activeSavePromise.catch(console.error); // Log but don't crash on save errors
          activeSavePromise = null; // Clear after completion
        }, debounceTime);
      }

      return nextState;
    };
};
